import { STATE_KEYS } from './StateConstants.js';

/**
 * Battle state management module.
 * Handles battle progress, resources, and defeated enemies.
 * Receives parent GameStateManager reference for state access and notifications.
 */
export class BattleState {
  constructor(parent) {
    this.parent = parent;
  }

  get state() {
    return this.parent.state;
  }

  _notify(key) {
    this.parent._notify(key);
  }

  _ensureBattleState() {
    if (!this.state.battle) {
      this.state.battle = { inProgress: false, resources: { gold: 0, mana: 0 }, defeatedEnemies: [] };
    }
  }

  // ==========================================
  // BATTLE PROGRESS
  // ==========================================

  isBattleInProgress() {
    return this.state.battle?.inProgress || false;
  }

  startBattle() {
    this._ensureBattleState();
    this.state.battle.inProgress = true;
    this.state.battle.resources = { gold: 0, mana: 0 };
    this.state.battle.defeatedEnemies = [];
    this.state.battle.buffs = { unitDamageBonus: 0, unitHealthBonus: 0 };
    this.parent.save();
  }

  endBattle() {
    this._ensureBattleState();
    this.state.battle.inProgress = false;
    this.resetBattleBuffs();
    this.parent.save();
  }

  // ==========================================
  // DEFEATED ENEMIES
  // ==========================================

  addDefeatedEnemy(enemyData) {
    this._ensureBattleState();
    if (!this.state.battle.defeatedEnemies) {
      this.state.battle.defeatedEnemies = [];
    }
    this.state.battle.defeatedEnemies.push(enemyData);
    this.parent.save();
  }

  getDefeatedEnemies() {
    return this.state.battle?.defeatedEnemies || [];
  }

  clearDefeatedEnemies() {
    if (this.state.battle) {
      this.state.battle.defeatedEnemies = [];
      this.parent.save();
    }
  }

  // ==========================================
  // BATTLE RESOURCES
  // ==========================================

  getBattleResources() {
    if (!this.state.battle?.resources) {
      this.state.battle = this.state.battle || {};
      this.state.battle.resources = { gold: 0, mana: 0 };
    }
    return this.state.battle.resources;
  }

  getBattleResource(type) {
    return this.getBattleResources()[type] || 0;
  }

  addBattleResource(type, amount) {
    const resources = this.getBattleResources();
    resources[type] = (resources[type] || 0) + amount;
    this._notify(STATE_KEYS.BATTLE_RESOURCES);
    return resources[type];
  }

  removeBattleResource(type, amount) {
    const resources = this.getBattleResources();
    const current = resources[type] || 0;
    const newAmount = Math.max(0, current - amount);
    resources[type] = newAmount;
    this._notify(STATE_KEYS.BATTLE_RESOURCES);
    return newAmount;
  }

  resetBattleResources() {
    if (this.state.battle) {
      this.state.battle.resources = { gold: 0, mana: 0 };
      this._notify(STATE_KEYS.BATTLE_RESOURCES);
    }
  }

  // ==========================================
  // DEPLOYED CARDS (units/buildings in play)
  // ==========================================

  getDeployedCards() {
    return this.state.battle?.deployedCards || [];
  }

  deployCard(cardId) {
    this._ensureBattleState();
    if (!this.state.battle.deployedCards) {
      this.state.battle.deployedCards = [];
    }
    this.state.battle.deployedCards.push(cardId);
    this.parent.save();
  }

  returnCard(cardId) {
    this._ensureBattleState();
    if (!this.state.battle.deployedCards) return;

    const index = this.state.battle.deployedCards.indexOf(cardId);
    if (index !== -1) {
      this.state.battle.deployedCards.splice(index, 1);
      this.parent.save();
    }
  }

  clearDeployedCards() {
    if (this.state.battle) {
      this.state.battle.deployedCards = [];
      this.parent.save();
    }
  }

  // ==========================================
  // CARDS IN HAND
  // ==========================================

  getCardsInHand() {
    return this.state.battle?.cardsInHand || [];
  }

  setCardsInHand(cardIds) {
    this._ensureBattleState();
    this.state.battle.cardsInHand = [...cardIds];
    this.parent.save();
  }

  addCardToHand(cardId) {
    this._ensureBattleState();
    if (!this.state.battle.cardsInHand) {
      this.state.battle.cardsInHand = [];
    }
    this.state.battle.cardsInHand.push(cardId);
    this.parent.save();
  }

  removeCardFromHand(cardId) {
    this._ensureBattleState();
    if (!this.state.battle.cardsInHand) return;

    const index = this.state.battle.cardsInHand.indexOf(cardId);
    if (index !== -1) {
      this.state.battle.cardsInHand.splice(index, 1);
      this.parent.save();
    }
  }

  clearCardsInHand() {
    if (this.state.battle) {
      this.state.battle.cardsInHand = [];
      this.parent.save();
    }
  }

  // ==========================================
  // BATTLE BUFFS (temporary buffs for current battle)
  // ==========================================

  getBattleBuffs() {
    return this.state.battle?.buffs || { unitDamageBonus: 0, unitHealthBonus: 0 };
  }

  addBattleBuff(buffType, value) {
    this._ensureBattleState();
    if (!this.state.battle.buffs) {
      this.state.battle.buffs = { unitDamageBonus: 0, unitHealthBonus: 0 };
    }
    this.state.battle.buffs[buffType] = (this.state.battle.buffs[buffType] || 0) + value;
    this._notify(STATE_KEYS.BATTLE_RESOURCES);
  }

  resetBattleBuffs() {
    if (this.state.battle) {
      this.state.battle.buffs = { unitDamageBonus: 0, unitHealthBonus: 0 };
    }
  }
}
